import { EventListener, View } from "silver";
import { html } from "core/Controls/Books/StudentBookControl.html";
import {
	AbstractBookControl,
	BookControlView,
	BookControlViewData,
	BookControlMetric
} from "core/Controls/Books/BookControl";
import { LanguageCodes } from "core/Misc";
import { Book as BookData, BookLength, getBookLength } from "core/Data/Book";

interface StudentBookControlViewData extends BookControlViewData {
	focused: boolean;
	showMyList: boolean;
	title: string;
	description: string;
	arQuizLevel: string;
	arQuizAvailable: boolean;
	audioRuntimeSecs: null|number;
	ribbonLabel: string|null;
	ribbonTitle: string|null;
	flagLabel: string|null;
	flagIcon: HTMLElement|null;
	flagClass: string|null;
	details: Array<{ element: HTMLElement }>
	atos: number|null;
	lexile: string|null;
}

interface StudentBookControlView extends BookControlView<StudentBookControlViewData> {
	coverCell: HTMLDivElement;
	controls: HTMLDivElement;
	ribbon: HTMLDivElement;
	myListButton: HTMLButtonElement;
}

export class StudentBookControl extends AbstractBookControl {

	public readonly onFocus = EventListener.create<{ sender: StudentBookControl }>("focus");
	public readonly onCheckChange = EventListener.create<{ sender: StudentBookControl }>("checkChange");
	public readonly onRequestInfo = EventListener.create<{ sender: StudentBookControl, originalEvent?: Event }>("requestInfo");
	public readonly onRequestRead = EventListener.create<{ sender: StudentBookControl, originalEvent?: Event }>("requestRead");
	public readonly onRequestArQuizLaunch = EventListener.create<{ sender: StudentBookControl, originalEvent?: Event }>("requestArQuizLaunch");
	private documentClickListener: (e: Event) => any;

	protected view: StudentBookControlView;

	constructor(
		showMyList: boolean,
		public readonly id: number
	) {
		super(id);
		this.view = new View<StudentBookControlViewData>(html, {
			getCoverAlt: (title: string): string => {
				if (! this.bookLang) {
					return "";
				}
				return this.bookLang.ALT_BOOK_COVER_IMG(title);
			},
			handleCheckChange: (): void => {
				this.onCheckChange.trigger({ sender: this });
			},
			getMyListLabel: (isOnMyList: boolean): string => {
				if (! this.bookLang || !this.bookData) {
					return "";
				}
				if (isOnMyList) {
					return this.bookLang.ACTION_REMOVE();
				}
				return this.bookLang.ACTION_ADD();
			},
			getMyListTitle: (isOnMyList: boolean): string => {
				if (! this.bookLang || !this.bookData) {
					return "";
				}
				if (isOnMyList) {
					return this.bookLang.ACTION_REMOVE_LONG();
				}
				return this.bookLang.ACTION_ADD_LONG();
			},
			formatMetricValue: <T>(metric: BookControlMetric<T>): string|Node => {
				if (this.bookData === undefined) {
					return "—";
				}
				const value = metric.value(this.bookData);
				if (value === null) {
					return "—";
				}
				return metric.valueFormatted(value as Exclude<T, null>, this.bookData);
			},
			handleArQuizButtonClicked: (event) => {
				this.onRequestArQuizLaunch.trigger({
					sender:        this,
					originalEvent: event
				});
			},
			handleInfoClicked: (event) => {
				this.onRequestInfo.trigger({
					sender:        this,
					originalEvent: event
				});
				event.stopPropagation();
			},
			handleMyListButtonClicked: (event: MouseEvent) => {
				if (!showMyList) {
					return;
				}
				this.setOnMyList(!this.onMyList, this, this.view.myListButton);
				event.stopPropagation();
			},
			handleReadButtonClicked: (event: MouseEvent) => {
				this.onRequestRead.trigger({
					sender:        this,
					originalEvent: event
				});
				event.stopPropagation();
			},
			handleControlFocused: () => {
				this.toggleFocused(true);
			},
			handleControlBlurred: () => {
				this.toggleFocused(false);
			}
		}, {
			lang:             { },
			focused:          false,
			title:            "",
			bookId:           id,
			showMyList,
			isOnMyList:       true,
			description:      "",
			arQuizLevel:      "",
			arQuizAvailable:  false,
			lexile:           null,
			audioRuntimeSecs: null,
			readLink:         "",
			atos:             null,
			card:             true,
			row:              false,
			checkbox:         false,
			lengthClass:      "",
			listMetrics:      [],
			listLabels:       [],
			ribbonTitle:      null,
			ribbonLabel:      null,
			flagIcon:         null,
			flagClass:        null,
			flagLabel:        null,
			details:          [],
			langString:       ""
		}) as StudentBookControlView;
		this.updateCoverURL();
		this.documentClickListener = (e: Event) => {
			if (!(e.target instanceof Node) || !this.view.element.contains(e.target)) {
				this.toggleFocused(false);
			}
		};
	}

	public setArQuizAvailable(arQuizAvailable: boolean): void {
		this.view.set({ arQuizAvailable });
	}

	private toggleFocused(focused: boolean): void {
		this.view.set({ focused });
		if (focused) {
			document.body.addEventListener("click", this.documentClickListener);
			this.onFocus.trigger({ sender: this });
		} else {
			document.body.removeEventListener("click", this.documentClickListener);
		}
	}

	get data(): BookData|undefined {
		return this.bookData;
	}

	/**
	 * We have to allow undefined data to match the `get data()` signature, but we won't accept it
	 */
	set data(data: BookData|undefined) {
		if (data === undefined || this.bookData) {
			return;
		}
		const length = getBookLength(data);
		const langIds = [...data.languageIds].sort((a, b) => b - a);
		const langId = langIds[0] || 1;
		this.bookData = data;

		this.view.set({
			title:            data.title,
			description:      data.description,
			arQuizLevel:      data.arQuizLevel ? String(data.archiveCode) : undefined,
			lexile:           data.lexileLabel,
			audioRuntimeSecs: data.audioRuntimeSecs,
			lengthClass:      length === BookLength.UNKNOWN ? "" : ("-" + length),
			isOnMyList:       data.onMyList,
			atos:             data.arQuizLevel,
			langString:       LanguageCodes[langId]
		});
		this.view.listMetrics.invalidate("metric");
		this.updateCoverURL();
		this.view.invalidate("isOnMyList");
	}

	public setRibbon(ribbonLabel: string, ribbonTitle: string): void {
		this.view.set({ ribbonLabel, ribbonTitle });
	}

	public setFlag(flagLabel: string, flagIcon: HTMLElement, flagClass: string): void {
		this.view.set({ flagLabel, flagIcon, flagClass });
	}

	public setDetails(details: HTMLElement[]): void {
		this.view.set({ details: details.map(element => ({ element })) });
	}
}
